import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Calendar } from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ViewTimesheet = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [timesheet, setTimesheet] = useState(null);

  useEffect(() => {
    const loadTimesheet = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${id}`, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load timesheet');
        }

        const data = await response.json();
        setTimesheet(data.data || null);
      } catch (err) {
        showError(err.message || 'Failed to load timesheet');
      } finally {
        setLoading(false);
      }
    };

    loadTimesheet();
  }, [id]);

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Timesheet Details"
          description="Loading timesheet details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!timesheet) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Timesheet Details"
          description="Timesheet not found"
        />
        <div className="page-container">
          <button
            type="button"
            className="btn btn-secondary d-flex align-items-center"
            onClick={() => navigate('/projects/time-tracking/timesheets')}
          >
            <ArrowLeft size={16} className="me-2" />
            Back to Timesheets
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Timesheet Details"
        description="Review timesheet information and daily entries"
      />
      <div className="page-container">
        <div className="project-create-container">
          <div className="project-form-card">
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Overview</h3>
              </div>
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Employee</label>
                  <div className="form-control">{timesheet.employeeName || '-'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <div className="form-control">{timesheet.department || '-'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Week Starting</label>
                  <div className="form-control">
                    {timesheet.weekStarting ? new Date(timesheet.weekStarting).toLocaleDateString() : '-'}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Week Ending</label>
                  <div className="form-control">
                    {timesheet.weekEnding ? new Date(timesheet.weekEnding).toLocaleDateString() : '-'}
                  </div>
                </div>
              </div>
            </div>

            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Summary</h3>
              </div>
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div className="form-control">{timesheet.status || 'draft'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Total Hours</label>
                  <div className="form-control">{timesheet.totalHours || 0}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Billable Hours</label>
                  <div className="form-control">{timesheet.billableHours || 0}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Non-Billable Hours</label>
                  <div className="form-control">{timesheet.nonBillableHours || 0}</div>
                </div>
              </div>
            </div>

            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Daily Entries</h3>
              </div>
              {(timesheet.timeEntries || []).map((day, index) => (
                <div key={day.date || index} className="mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <Calendar size={16} className="me-2" />
                    <strong>
                      {day.dayName || ''} {day.date ? `- ${new Date(day.date).toLocaleDateString()}` : ''}
                    </strong>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Description</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Hours</th>
                          <th>Billable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(day.entries || []).map((entry, entryIndex) => (
                          <tr key={entry.id || entryIndex}>
                            <td>{entry.projectName || '-'}</td>
                            <td>{entry.description || '-'}</td>
                            <td>{entry.startTime || '-'}</td>
                            <td>{entry.endTime || '-'}</td>
                            <td>{entry.duration || 0}</td>
                            <td>{entry.billable ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-secondary d-flex align-items-center"
              onClick={() => navigate('/projects/time-tracking/timesheets')}
            >
              <ArrowLeft size={16} className="me-2" />
              Back to Timesheets
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center"
              onClick={() => navigate(`/projects/time-tracking/timesheets/edit/${id}`)}
            >
              <Edit size={16} className="me-2" />
              Edit Timesheet
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTimesheet;
